import noSelectedText from "Common/componentUtils/noSelectedText";
import { ButtonWithTooltip } from "Components/button";
import Scrollbars from "Components/scrollbars";
import SlideUpDown from "Components/slideUpDown";
import { themeVariables } from "Components/theme";
import WelcomePanel from "Components/welcomePanel";
import classNames from "classnames";
import findIndex from "lodash/findIndex";
import includes from "lodash/includes";
import reject from "lodash/reject";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";
import EdgeLabel from "../../edgeLabel";
import NodeLabel from "../../nodeLabel";
import isDocumentObject from "../../utils/isDocumentObject";
import isTransform from "../../utils/isTransform";
import EdgeAttributes from "./edgeAttributes";
import locale from "./innerPanel.locale";
import NodeAttributes from "./nodeAttributes";
import Properties from "./properties";
const innerObjectLabelClassName = "line-height-1 v-center-items pl-xs pr-xs pt-0_75";
const buildBlockHeader = (text, closedBlocks, buildClickCallback) => (React.createElement("div", { className: "bg-gray-450 fw-6 clickable pl-sm pr-sm pt-0_25 pb-0_25", onClick: buildClickCallback(text) },
    React.createElement("i", { className: classNames("text-light-on-surface-variant font-bolder font-small mr-0_75 icon-arrow-up", includes(closedBlocks, text) && "rotate-icon-left-90") }),
    text));
export default function InnerPanel(props) {
    const [closedBlocks, setClosedBlocks] = useState([]);
    const [data, setData] = useState([]);
    const panelId = useMemo(() => `id${uuid()}`, []);
    useEffect(() => {
        setData(props.data || []);
    }, [props.data]);
    const singleSelectedNodeSource = useMemo(() => data.length === 1 && isDocumentObject(data[0]) ? data[0] : undefined, [data]);
    const singleSelectedEdgeSource = useMemo(() => (data.length === 1 && isTransform(data[0]) ? data[0] : undefined), [data]);
    const updateCallback = useCallback((updated) => {
        if (!updated)
            return;
        setData(prev => {
            const index = findIndex(prev, ["Id", updated.Id]);
            if (index === -1)
                return prev;
            const result = [...prev];
            result[index] = updated;
            return result;
        });
    }, []);
    const handleDataUpdate = useCallback((documentObjectId, attributeId, fieldName, value) => {
        if (!props.onDataUpdate)
            return;
        updateCallback(props.onDataUpdate(documentObjectId, attributeId, fieldName, value));
    }, [props.onDataUpdate, Boolean(singleSelectedNodeSource), updateCallback]);
    const handlePropertyUpdate = useCallback((documentObjectId, fieldName, value) => {
        handleDataUpdate(documentObjectId, undefined, fieldName, value);
    }, [handleDataUpdate]);
    const handleAttributeDelete = useCallback((documentObjectId, attributeId) => {
        handleDataUpdate(documentObjectId, attributeId, undefined, undefined);
    }, [handleDataUpdate]);
    const handleAttributeAdd = useCallback((documentObjectId, attribute) => {
        if (!props.onAttributeAdd)
            return;
        updateCallback(props.onAttributeAdd(documentObjectId, attribute));
    }, [props.onAttributeAdd, updateCallback]);
    const handleAttributesSort = useCallback((documentObjectId, attributes) => {
        if (!props.onAttributesSort)
            return;
        updateCallback(props.onAttributesSort(documentObjectId, attributes));
    }, [props.onAttributeAdd, updateCallback]);
    const handleBlockClick = (block) => () => {
        noSelectedText() &&
            setClosedBlocks(prev => includes(prev, block) ? reject(prev, e => e === block) : [...prev, block]);
    };
    const attributesBlock = useMemo(() => singleSelectedNodeSource || singleSelectedEdgeSource ? (React.createElement("div", { className: "h-100-max overflow-hidden grid-rows-auto-1fr", key: `${singleSelectedNodeSource === null || singleSelectedNodeSource === void 0 ? void 0 : singleSelectedNodeSource.Id}_${singleSelectedEdgeSource === null || singleSelectedEdgeSource === void 0 ? void 0 : singleSelectedEdgeSource.Id}` },
        buildBlockHeader(locale.ATTRIBUTES, closedBlocks, handleBlockClick),
        React.createElement(Scrollbars, { key: (singleSelectedNodeSource === null || singleSelectedNodeSource === void 0 ? void 0 : singleSelectedNodeSource.Id) || (singleSelectedEdgeSource === null || singleSelectedEdgeSource === void 0 ? void 0 : singleSelectedEdgeSource.Id) },
            React.createElement("div", { className: "stretched-to-fit" },
                React.createElement(SlideUpDown, { open: !includes(closedBlocks, locale.ATTRIBUTES), mountOnlyAfterFirstOpening: false },
                    singleSelectedNodeSource && (React.createElement(NodeAttributes, { key: singleSelectedNodeSource.Id, documentObjectId: singleSelectedNodeSource.Id, attributes: singleSelectedNodeSource.Attributes, onDataUpdate: props.canModifyAttributes && props.onDataUpdate ? handleDataUpdate : undefined, onDelete: props.canModifyAttributes && props.onDataUpdate
                            ? handleAttributeDelete
                            : undefined, onAdd: props.canModifyAttributes && props.onAttributeAdd
                            ? handleAttributeAdd
                            : undefined, generateAttributeId: props.generateAttributeId, onSort: props.canModifyAttributes && props.onAttributesSort
                            ? handleAttributesSort
                            : undefined, zIndex: props.zIndex })),
                    singleSelectedEdgeSource && (React.createElement(EdgeAttributes, { key: singleSelectedEdgeSource.Id, data: singleSelectedEdgeSource, from: props.transformFrom }))))))) : (React.createElement("div", null)), [
        singleSelectedNodeSource,
        singleSelectedEdgeSource,
        closedBlocks,
        props.canModifyAttributes,
        props.onDataUpdate,
        props.onAttributeAdd,
        props.onAttributesSort,
        handleDataUpdate,
        handleAttributeDelete,
        handleAttributeAdd,
        props.generateAttributeId,
        handleAttributesSort,
        props.zIndex
    ]);
    const topLabel = useMemo(() => {
        if (singleSelectedNodeSource)
            return (React.createElement(NodeLabel, { imgSize: 50, data: singleSelectedNodeSource, className: innerObjectLabelClassName }));
        if (singleSelectedEdgeSource)
            return (React.createElement(EdgeLabel, { imgSize: 50, data: singleSelectedEdgeSource, from: props.transformFrom, to: props.transformTo, className: innerObjectLabelClassName }));
        return null;
    }, [
        data,
        props.transformFrom,
        props.transformTo,
        singleSelectedNodeSource,
        singleSelectedEdgeSource
    ]);
    const handleDeleteDocumentObject = useCallback(() => {
        if (singleSelectedNodeSource) {
            props.onDeleteDocumentObject && props.onDeleteDocumentObject(singleSelectedNodeSource.Id);
        }
    }, [props.onDeleteDocumentObject, singleSelectedNodeSource]);
    const needDeleteButton = Boolean(singleSelectedNodeSource && props.onDeleteDocumentObject);
    return (React.createElement("div", { className: classNames("h-100 h-100-max overflow-hidden", (singleSelectedNodeSource || singleSelectedEdgeSource) && "grid-rows-auto-1fr"), id: panelId },
        (singleSelectedNodeSource || singleSelectedEdgeSource) && (React.createElement(React.Fragment, null,
            React.createElement("div", null,
                React.createElement("div", { className: "caption-with-actions-panel v-center-items" },
                    topLabel,
                    needDeleteButton && (React.createElement("div", { className: "pr-1" },
                        React.createElement(ButtonWithTooltip, { className: "no-border no-default-colors animate-hover-color", width: themeVariables["font-size-h3"], height: themeVariables["font-size-h3"], onClick: handleDeleteDocumentObject, popoverContent: locale.DELETE_TOOLTIP, zIndex: props.zIndex },
                            React.createElement("i", { className: "icon-trash-filled font-h5 text-red" }))))),
                React.createElement(Properties, { className: "p-xs", data: singleSelectedNodeSource || singleSelectedEdgeSource, onDataUpdate: props.onDataUpdate && singleSelectedNodeSource ? handlePropertyUpdate : undefined })),
            attributesBlock)),
        data.length > 1 && (React.createElement(WelcomePanel, { messageClassName: "font-base", showIcon: false, message: locale.MORE_THAN_ONE_OBJECT_SELECTED }))));
}
